/**
 * KPI Store - 메인 상태 관리 (리팩토링 버전)
 *
 * @description 검색 조건, KPI 목록, 옵션 로드 등 핵심 기능만 담당
 *
 * 분리된 Store:
 * - useKpiDetailStore: 월별 실적 CRUD
 * - useKpiFormStore: KPI 추가/수정 폼
 * - useOkrStore: OKR 센터패널 + 피드백
 * - useRefKpiStore: 참고 KPI 모달
 *
 * @usage
 * const { selectedYear, kpiList } = useKpiStore();
 * const { handleSearch, fetchKpiData } = useKpiMethodStore();
 */
import { watch } from 'vue';
import { useKpiApi } from '~/composables/performance/kpi/useKpiApi';
import { useCommonStore } from '~/stores/useCommonStore';
import { useUiToast } from '~/composables/useUiToast';
import { kpiT } from '~/composables/performance/kpi/useKpiI18n';

// OKR Store import
import { useOkrStore } from './useOkrStore';

// 공유 상태 import
import {
  selectedYear,
  selectedDept,
  selectedMember,
  activeMember,
  isLeader,
  yearOptions,
  deptOptions,
  memberOptions,
  loginUserInfo,
  memberTree,
  memberShareList,
  teamMemberCache,
  kpiStatusList,
  kpiList,
  isLoading,
  pendingCount,
  selectedKpi,
  activeDetailTab,
  resetDetailData,
  goalTypeFilter,
  // draft 상태 (UI 입력용 임시 값 - 검색 버튼 클릭 전까지 API 조회에 영향 없음)
  draftYear,
  draftDept,
  draftMember,
  draftGoalTypeFilter,
  // draft → applied 복사 함수
  applySearchConditions,
  // KPI 합의(Agreement)
  agreementYn,
  agreementData,
  agreementHistoryList,
  managerCnt,
  kpiConfigTermYn,
  agreementEditYn,
  selectedMemberAccessType,
  // 선택 대상자 열람 허용 도메인 (KPI↔OKR 교차 열람 누수 방지)
  selectedMemberDomains,
  selectedMemberDomainScope,
  allowedGoalTypeOptions,
  enforceGoalTypeForDomains,
} from './useKpiSharedState';

// 공통 유틸리티 import
import { mapKpiListItem } from './useKpiUtils';

// 타입 import
import type {
  KpiItem,
  MemberTreeItem,
  MemberOption,
  KpiStatusResponseItem,
  DeptListResponseItem,
  UserListResponseItem,
  MemberTreeResponseItem,
} from './useKpiTypes';

// ============================================
// State Store Export
// ============================================
/**
 * KPI Store - 상태 관리
 * @description KPI 관련 모든 상태를 제공하는 composable
 * @returns KPI 상태 객체 (검색 조건, 옵션, 목록, 실적상세, OKR, Form 상태 포함)
 */
export const useKpiStore = () => {
  return {
    // 검색 조건 (applied - 실제 API 조회에 쓰이는 확정 값)
    selectedYear,
    selectedDept,
    selectedMember,
    activeMember,

    // 검색 조건 (draft - UI 셀렉트/패널에서 임시로 고른 값, 검색 버튼 클릭 전)
    draftYear,
    draftDept,
    draftMember,
    draftGoalTypeFilter,

    // 관리자 여부
    isLeader,

    // 옵션 목록
    yearOptions,
    deptOptions,
    memberOptions,

    // 로그인 사용자 정보
    loginUserInfo,

    // MemberPanel
    memberTree,
    memberShareList,
    teamMemberCache,

    // KPI 합의(Agreement)
    agreementYn,
    agreementData,
    agreementHistoryList,
    managerCnt,
    kpiConfigTermYn,
    agreementEditYn,
    selectedMemberAccessType,

    // 헤더 배지
    pendingCount,

    // KPI 상태 목록
    kpiStatusList,

    // KPI 목록
    kpiList,
    isLoading,

    // 실적상세 기본 (useKpiStore 고유)
    selectedKpi,
    activeDetailTab,

    // 목표 유형 필터 (applied)
    goalTypeFilter,

    // 선택 대상자 열람 허용 도메인 (KPI↔OKR 교차 열람 누수 방지)
    selectedMemberDomains,
    selectedMemberDomainScope,
    allowedGoalTypeOptions,
    enforceGoalTypeForDomains,
  };
};

// ============================================
// Method Store Export
// ============================================

/** Watch 중복 등록 방지 플래그 */
let watchersRegistered = false;

/**
 * KPI Store - 메서드 관리
 * @description KPI 관련 모든 메서드를 제공하는 composable
 * @returns KPI 메서드 객체 (옵션 로드, 목록 조회, 실적상세, OKR, Form 메서드 포함)
 */
export const useKpiMethodStore = () => {
  const commonStore = useCommonStore();
  const kpiApi = useKpiApi();
  const okrStore = useOkrStore();

  // ============================================
  // KPI 합의(Agreement) - 공통 플래그 갱신
  // ============================================

  /**
   * 합의 기능 사용 여부(agreementYn) 갱신
   *
   * - 설정(confMng)의 "KPI 합의기능 사용여부"를 AS-IS API로 그대로 조회합니다.
   * - 응답이 'Y'/'N' 문자열이라서 여기서 boolean으로 변환해 보관합니다.
   */
  const refreshAgreementYn = async () => {
    try {
      const res: any = await kpiApi.fetchAgreementYn(selectedYear.value);
      const yn = res?.data?.agreementYn ?? res?.agreementYn;
      agreementYn.value = String(yn || '').toUpperCase() === 'Y';
    } catch (error) {
      console.error('합의 기능 사용여부 조회 실패:', error);
      agreementYn.value = false;
    }
  };

  /**
   * KPI 등록/수정 기간 여부(kpiConfigTermYn) 갱신
   *
   * ⚠️ 중요\n
   * - 기간이 만료되면 KPI 저장/수정/삭제 및 합의 버튼까지 전부 차단해야 합니다.\n
   * - AS-IS는 `kpi.do`의 `kpiConfigTermYn`을 사용하므로 동일하게 호출합니다.\n
   * - `kpiId`는 선택이며, 가능하면 목록의 첫 KPI를 넣어 호출합니다(AS-IS와 동일한 맥락 유지).\n
   */
  const refreshKpiConfigTermYn = async (kpiId?: string) => {
    try {
      const res: any = await kpiApi.fetchKpiConfigTermYn(selectedYear.value, kpiId);
      const yn = res?.data?.kpiConfigTermYn ?? res?.kpiConfigTermYn;
      kpiConfigTermYn.value = String(yn || '').toUpperCase() === 'Y';
    } catch (error) {
      console.error('KPI 등록/수정 기간 여부 조회 실패:', error);
      // 실패 시에는 사용자를 과하게 막지 않도록 true로 두되,
      // 실제 서버 검증에서 막히는 케이스는 저장 API가 실패할 수 있습니다.
      kpiConfigTermYn.value = true;
    }
  };

  // ============================================
  // 옵션 로드
  // ============================================

  /**
   * 연도 옵션 로드
   * @description 공통 Store에서 연도 목록을 가져와 yearOptions에 저장
   * @throws 실패 시 최근 3년 기본값 설정
   */
  const loadYearOptions = async () => {
    try {
      const result = await commonStore.getYearList(); // 연도 목록 조회
      yearOptions.value = result || []; // yearOptions에 저장
    } catch (error) {
      console.error('년도 옵션 로드 실패:', error);
      const currentYear = new Date().getFullYear();
      yearOptions.value = [
        { label: String(currentYear), value: String(currentYear) },
        { label: String(currentYear - 1), value: String(currentYear - 1) },
        { label: String(currentYear - 2), value: String(currentYear - 2) },
      ];
    }
  };

  /**
   * KPI 진행상태 목록 조회
   * @description 선택된 연도의 KPI 진행상태 목록을 조회하여 kpiStatusList에 저장
   */
  const fetchKpiStatusListData = async () => {
    try {
      const response = await kpiApi.fetchKpiStatusList(selectedYear.value);
      const statusList = response?.data?.kpiStatusList || response?.kpiStatusList;

      if (statusList && Array.isArray(statusList)) {
        kpiStatusList.value = statusList.map((item: KpiStatusResponseItem) => ({
          statusCd: item.statusCd || '',
          statusNm: item.statusNm || '',
          color: item.color || '#6b7280',
          progressRate: item.progressRate,
          sortOrder: item.sortOrder,
        }));
      }

      // 같은 응답에 OKR(KR) 상태 목록(statusList)도 들어 있다.
      // KR 목록 API 가 statusNm 을 비워 보내는 경우가 있어 코드→이름 변환에 쓰인다.
      // 원래는 KR 피드백 조회 시점에만 채워져 그리드 렌더 시점엔 비어 있었다.
      const okrStatuses = response?.data?.statusList || response?.statusList;
      if (Array.isArray(okrStatuses) && okrStatuses.length > 0) {
        okrStore.okrStatusOptions.value = okrStatuses.map((item: KpiStatusResponseItem) => ({
          value: item.statusCd || '',
          label: item.statusNm || '',
          color: item.color || '#999',
        }));
      }
    } catch (error) {
      console.error('KPI 진행상태 목록 조회 실패:', error);
    }
  };

  /**
   * 부서 옵션 로드
   * @description 관리자인 경우 전체 부서 목록, 일반 사용자인 경우 본인 부서만 로드
   */
  const loadDeptOptions = async () => {
    try {
      if (isLeader.value) {
        const result = await kpiApi.fetchKpiDeptList(); // 부서 목록 조회

        if (result?.list && Array.isArray(result.list)) {
          deptOptions.value = [
            { label: '전체', value: '' },
            ...result.list.map((item: DeptListResponseItem) => ({
              label: item.deptNm || item.deptFNm || '',
              value: item.deptId || '',
            })),
          ];
          if (loginUserInfo.value?.deptId) {
            // applied + draft 동기화 (초기 로드)
            selectedDept.value = loginUserInfo.value.deptId;
            draftDept.value = loginUserInfo.value.deptId;
          }
        } else {
          deptOptions.value = [{ label: '전체', value: '' }];
        }
      } else {
        if (loginUserInfo.value) {
          deptOptions.value = [
            {
              label: loginUserInfo.value.deptNm || '',
              value: loginUserInfo.value.deptId || '',
            },
          ];
          // applied + draft 동기화 (초기 로드)
          selectedDept.value = loginUserInfo.value.deptId || '';
          draftDept.value = loginUserInfo.value.deptId || '';
        }
      }
    } catch (error) {
      console.error('부서 옵션 로드 실패:', error);
      deptOptions.value = [{ label: '전체', value: '' }];
    }
  };

  /**
   * 구성원 옵션 로드
   * @param deptId - 부서 ID (선택사항)
   * @param autoSelectMe - true인 경우 로그인 사용자를 activeMember로 자동 선택
   * @description 관리자인 경우 전체 구성원 목록, 일반 사용자인 경우 본인만 로드
   */
  const loadMemberOptions = async (deptId?: string, autoSelectMe: boolean = false) => {
    try {
      if (isLeader.value) {
        const result = await kpiApi.fetchKpiUserList(deptId || undefined);

        if (result?.list && Array.isArray(result.list)) {
          memberOptions.value = [
            { label: '전체', value: '', posNm: '', deptNm: '' },
            ...result.list.map((item: UserListResponseItem) => ({
              label: item.userNm || '',
              value: item.userId || '',
              posNm: item.posNm || '',
              deptNm: item.deptNm || '',
            })),
          ];
          if (autoSelectMe && loginUserInfo.value?.userId) {
            // applied + draft 동기화 (초기 로드)
            selectedMember.value = '';
            draftMember.value = '';
            activeMember.value = loginUserInfo.value.userId;
          }
        } else {
          memberOptions.value = [{ label: '전체', value: '', posNm: '', deptNm: '' }];
        }
      } else {
        if (loginUserInfo.value) {
          memberOptions.value = [
            {
              label: loginUserInfo.value.userNm || '',
              value: loginUserInfo.value.userId || '',
              posNm: loginUserInfo.value.posNm || '',
              deptNm: loginUserInfo.value.deptNm || '',
            },
          ];
          // applied + draft 동기화 (비관리자 초기 로드)
          selectedMember.value = loginUserInfo.value.userId || '';
          draftMember.value = loginUserInfo.value.userId || '';
          activeMember.value = loginUserInfo.value.userId || '';
        }
      }
    } catch (error) {
      console.error('구성원 옵션 로드 실패:', error);
      memberOptions.value = [{ label: '전체', value: '', posNm: '', deptNm: '' }];
    }
  };

  /**
   * 구성원 트리 조회
   * @param findDeptId - 조회할 부서 ID (선택사항)
   * @param findUserId - 조회할 사용자 ID (선택사항)
   * @description 조직도 형태의 구성원 트리를 조회하여 memberTree에 저장
   */
  const fetchMemberTree = async (findDeptId?: string, findUserId?: string) => {
    try {
      const response = await kpiApi.fetchMemberTreeList(
        selectedYear.value || new Date().getFullYear().toString(),
        findDeptId || undefined,
        findUserId || undefined,
      );

      if (!response?.memberTreeList?.length) {
        memberTree.value = [];
        return;
      }

      // 1단계: parentId 추출 + 가공
      const allMembers = response.memberTreeList.map((item: MemberTreeResponseItem) => {
        const rawParentId =
          item.UP_KEY_ID || item.upKeyId || item.PARENT_ID || item.parentId || null;
        let parentUserId = null;
        if (rawParentId && rawParentId.includes('_')) {
          parentUserId = rawParentId.split('_')[0] || null; // "isp123_D000001" → "isp123"
        } else {
          parentUserId = rawParentId;
        }

        return {
          userId: item.USER_ID || item.userId || '',
          userNm: item.USER_NM || item.userNm || '',
          posNm: item.POS_NM || item.posNm || item.JIKGUB_NM || item.jikgubNm || '',
          deptNm: item.DEPT_NM || item.deptNm || '',
          parentId: parentUserId,
          kpiRate: Number(item.KPI_RATE || item.kpiRate) || 0,
          isMe: item.IS_ME === 'Y' || item.isMe === 'Y',
          profileImg: null,
        };
      });

      // 2단계: 중복 제거
      const seen = new Set<string>();
      const uniqueMembers = allMembers.filter((m: MemberTreeItem) => {
        if (!m.userId || seen.has(m.userId)) return false;
        seen.add(m.userId);
        return true;
      });

      // 3단계: 저장
      memberTree.value = uniqueMembers;
    } catch (error) {
      console.error('구성원 트리 로드 실패:', error);
      memberTree.value = [];
    }
  };

  /**
   * 구성원/공유자 목록 조회 (MemberPanel 탭용)
   * @param shareTypeCd - 조회 타입 ('team': 구성원, 'share': 공유자)
   * @description KPI(/hcm/kpi/kpiMng/shareUserList.do) + OKR(/hcm/kpi/kpiMng/selectSearchUserData_json.do)
   *              두 도메인 모두에서 나를 매니저/공유자로 등록한 사용자를 가져와 userId 기준으로 합친다.
   *              KPI만 호출하던 기존 동작에선 OKR 매니저/공유자 등록자가 누락됨.
   *
   *              ⚠️ 각 사용자에 등록 출처 도메인을 태깅한다(kpi/okr 플래그).
   *              KPI 공유자로만 등록된 사용자를 선택했을 때 OKR까지 열람되는 누수를
   *              막기 위함 — 단순 합집합(userId만)으로 합치면 도메인 차원이 소실된다.
   */
  /**
   * 구성원/공유자 목록 조회 + 도메인 병합 (순수 조회 — 상태를 변경하지 않음)
   *
   * @description loadMemberShareList(목록 표시용)와 resolveMemberDomains(권한 재확인용)이
   *              같은 병합 규칙을 쓰도록 분리했다. 권한 재확인 쪽이 memberShareList를
   *              덮어쓰면 좌측 패널에 표시 중인 탭 목록이 흔들리기 때문에 상태는 건드리지 않는다.
   * @param shareTypeCd - 조회 타입 ('team': 구성원, 'share': 공유자)
   * @param findYear - 조회 연도 (권한은 연도별로 다르므로 반드시 명시)
   */
  const fetchMergedShareUsers = async (shareTypeCd: 'team' | 'share', findYear: string) => {
    // KPI + OKR 두 API 병렬 호출
    // OKR API의 findCycleId 는 SQL 매퍼에서 사용되지 않으므로 빈 문자열 전달
    const [kpiResp, okrResp] = await Promise.all([
      kpiApi.fetchShareUserList(findYear, undefined, shareTypeCd),
      kpiApi.fetchMemberShareList(findYear, '', shareTypeCd),
    ]);

    const kpiList = kpiResp?.shareUserList || kpiResp?.searchUserList || [];
    const okrList = okrResp?.searchUserList || okrResp?.shareUserList || [];

    type RawUser = {
      userId?: string;
      userNm?: string;
      SEARCH_USER_ID?: string;
      searchUserId?: string;
      SEARCH_USER_NM?: string;
      searchUserNm?: string;
      JIKGUB_NM?: string;
      jikgubNm?: string;
      DEPT_NM?: string;
      deptNm?: string;
      // 합의상태: selectShareUserList(shareUserList.do)가 이미 반환 — 구성원 탭 배지 표시용
      AGREEMENT_CD?: string;
      agreementCd?: string;
      AGREEMENT_NM?: string;
      agreementNm?: string;
    };

    // domain: 이 사용자가 어느 도메인에서 나를 등록했는지(kpi/okr 플래그 태깅)
    const mapItem = (item: RawUser, domain: 'kpi' | 'okr') => ({
      searchUserId: item.userId || item.SEARCH_USER_ID || item.searchUserId || '',
      searchUserNm: item.userNm || item.SEARCH_USER_NM || item.searchUserNm || '',
      posNm: item.JIKGUB_NM || item.jikgubNm || '',
      deptNm: item.DEPT_NM || item.deptNm || '',
      // OKR 병합 사용자는 합의코드가 없음 → 배지 미표시(정상)
      agreementCd: item.AGREEMENT_CD || item.agreementCd || '',
      agreementNm: item.AGREEMENT_NM || item.agreementNm || '',
      kpi: domain === 'kpi',
      okr: domain === 'okr',
    });

    const kpiMapped = (Array.isArray(kpiList) ? kpiList : []).map((i: RawUser) => mapItem(i, 'kpi'));
    const okrMapped = (Array.isArray(okrList) ? okrList : []).map((i: RawUser) => mapItem(i, 'okr'));

    // userId 기준 병합 — 중복 시 "도메인 플래그를 OR" 로 합쳐 권한 차원을 보존한다.
    // (KPI 먼저 처리 → 합의상태(agreementCd)는 KPI 응답 값이 우선 유지됨)
    const byId = new Map<string, ReturnType<typeof mapItem>>();
    for (const u of [...kpiMapped, ...okrMapped]) {
      if (!u.searchUserId) continue;
      const prev = byId.get(u.searchUserId);
      if (prev) {
        prev.kpi = prev.kpi || u.kpi;
        prev.okr = prev.okr || u.okr;
        prev.posNm = prev.posNm || u.posNm;
        prev.deptNm = prev.deptNm || u.deptNm;
        if (!prev.agreementCd && u.agreementCd) {
          prev.agreementCd = u.agreementCd;
          prev.agreementNm = u.agreementNm;
        }
      } else {
        byId.set(u.searchUserId, { ...u });
      }
    }
    return Array.from(byId.values());
  };

  const loadMemberShareList = async (shareTypeCd: 'team' | 'share' = 'team') => {
    try {
      memberShareList.value = await fetchMergedShareUsers(shareTypeCd, selectedYear.value);

      // team 조회 시 캐시에도 저장 (조직탭/검색에서 accessType 판별용)
      if (shareTypeCd === 'team') {
        teamMemberCache.value = [...memberShareList.value];
      }
    } catch (error) {
      console.error('구성원/공유자 목록 로드 실패:', error);
      memberShareList.value = [];
    }
  };

  /**
   * 검색 시점의 열람 도메인 재확인 (구성원/공유 탭 경유 선택 전용)
   *
   * @description 권한은 연도별로 다르므로 "현재 적용된 연도" 기준으로 다시 조회한다.
   *              구성원/공유 목록은 탭 전환 시에만 로드되어 연도 변경 후에는 이전 연도
   *              기준이므로, 캐시를 그대로 믿으면 잘못된 연도의 권한으로 판정된다.
   * @returns 열람 허용 도메인. 등록 내역이 없거나 조회 실패 시 모두 차단(fail-closed)
   */
  const resolveMemberDomains = async (userId: string, shareTypeCd: 'team' | 'share') => {
    try {
      const list = await fetchMergedShareUsers(shareTypeCd, selectedYear.value);
      const entry = list.find(u => u.searchUserId === userId);
      return entry ? { kpi: !!entry.kpi, okr: !!entry.okr } : { kpi: false, okr: false };
    } catch (error) {
      console.error('열람 도메인 재확인 실패:', error);
      return { kpi: false, okr: false };
    }
  };

  // ============================================
  // KPI 목록 헬퍼
  // ============================================

  /**
   * 공유받은 KPI들의 실적/달성률 동기화
   * @description 공유받은 KPI(shareGbn === 'shared')의 경우 원본 KPI의 실적 데이터를 조회하여 동기화
   */
  const syncKpiCumulativeData = async () => {
    if (kpiList.value.length === 0) return;

    // 공유받은 KPI만 필터링 (shareGbn === 'shared')
    const sharedKpis = kpiList.value
      .map((kpi, index) => ({ kpi, index }))
      .filter(({ kpi }) => kpi.shareGbn === 'shared' && kpi.upKpiId);

    // 공유받은 KPI가 없으면 스킵
    if (sharedKpis.length === 0) return;

    const syncPromises = sharedKpis.map(async ({ kpi, index }) => {
      try {
        const response = await kpiApi.fetchActualList(
          selectedYear.value,
          selectedMember.value || '',
          kpi.upKpiId || '', // 원본 KPI의 실적 조회
        );

        if (
          response?.actualList &&
          Array.isArray(response.actualList) &&
          response.actualList.length > 0
        ) {
          // accrueMethodCd에 따라 프론트에서 재계산 (fetchMonthlyRecords와 동일)
          const actuals = response.actualList.filter(
            (item: any) => item.seq === 0 || item.seq === '0',
          );
          if (actuals.length === 0) return null;

          const accrueMethod = kpi.accrueMethodCd || '001';
          const target = Number(actuals[0]?.target) || 0;
          const isQualitative = kpi.kpiTypeCd === '02';

          // 비계량: 최신 월 진척률
          if (isQualitative) {
            const latestProgress = Number(actuals[0]?.achive) || 0;
            return { index, accrueAchieve: latestProgress, actualStr: String(latestProgress) };
          }

          // 계량: accrueMethodCd에 따라 누적 계산
          const sorted = [...actuals].sort((a, b) => Number(a.mon) - Number(b.mon));
          let runningSum = 0;
          let accumActual = 0;
          sorted.forEach((item, idx) => {
            const actual = Number(item.actual) || 0;
            runningSum += actual;
            if (accrueMethod === '003') accumActual = actual;
            else if (accrueMethod === '002')
              accumActual = parseFloat((runningSum / (idx + 1)).toFixed(2));
            else accumActual = runningSum;
          });
          const accumRate = target ? parseFloat(((accumActual / target) * 100).toFixed(2)) : 0;

          return { index, accrueAchieve: accumRate, actualStr: String(accumActual) };
        }
        return null;
      } catch (error) {
        console.error('[syncKpiCumulativeData] 동기화 실패:', kpi.kpiId, error);
        return null;
      }
    });

    const results = await Promise.all(syncPromises);
    const validResults = results.filter(
      (r): r is { index: number; accrueAchieve: number; actualStr: string } => r !== null,
    );

    if (validResults.length > 0) {
      const newList = [...kpiList.value];
      validResults.forEach(({ index, accrueAchieve, actualStr }) => {
        newList[index] = {
          ...newList[index],
          accrueAchieve,
          actualStr,
        };
      });
      kpiList.value = newList;
    }
  };

  /**
   * KPI 복사 실행 (다른 연도로 KPI 마스터 행만 복사)
   * @description AS-IS copyKpi — hcm_kpi 마스터만 INSERT. 하위 OKR/업무/실적은 복사되지 않음.
   * @param payload.kpiId - 복사할 원본 KPI ID
   * @param payload.copyYear - 대상 연도
   */
  const handleCopyKpi = async (payload: { kpiId: string; copyYear: string }) => {
    const toast = useUiToast();
    if (!payload.copyYear) {
      toast.error(kpiT('toast.yearSelectForKpi'));
      throw new Error('copyYear required');
    }
    try {
      await kpiApi.copyKpi(selectedYear.value, payload.copyYear, payload.kpiId);
      toast.success(kpiT('toast.kpiCopySuccess'));
    } catch (error) {
      console.error('KPI 복사 실패:', error);
      // useApi 자동 에러 토스트와 중복되지 않도록 store toast 생략
      throw error;
    }
  };

  // ============================================
  // KPI 목록
  // ============================================

  /**
   * KPI 목록 조회
   * @param preserveSelection - true이면 현재 선택된 KPI를 유지 (저장 후 새로고침 시 사용)
   * @description 선택된 연도/구성원의 KPI 목록을 조회하고, 받은 목표 개수와 진행상태 목록도 함께 업데이트
   */
  const fetchKpiData = async (preserveSelection: boolean = false) => {
    // 선택된 KPI ID 저장 (preserveSelection이 true일 때)
    const previousSelectedKpiId = preserveSelection ? selectedKpi.value?.kpiId : null;

    try {
      isLoading.value = true;

      const userId = selectedMember.value || loginUserInfo.value?.userId || undefined;
      const response = await kpiApi.fetchKpiList(selectedYear.value, userId);

      if (response?.kpiList && Array.isArray(response.kpiList)) {
        kpiList.value = response.kpiList.map(mapKpiListItem);
      } else {
        kpiList.value = [];
      }

      // ------------------------------------------------------------
      // KPI 합의(Agreement) 데이터 적재 (AS-IS kpiList.do 응답에 포함)
      // ------------------------------------------------------------
      // agreementYn이 'Y'일 때만 agreementData/history가 내려오므로,
      // 1) agreementYn은 별도 API로 갱신\n
      // 2) 여기서는 "내려온 것이 있으면" 저장하고, 없으면 초기화\n
      // ------------------------------------------------------------
      const agreementDataFromApi = response?.agreementData ?? response?.data?.agreementData ?? null;
      const agreementHistoryFromApi =
        response?.agreementHistoryList ?? response?.data?.agreementHistoryList ?? [];
      const managerCntFromApi = response?.managerCnt ?? response?.data?.managerCnt ?? 0;

      agreementData.value = agreementDataFromApi || null;
      agreementHistoryList.value = Array.isArray(agreementHistoryFromApi)
        ? agreementHistoryFromApi
        : [];
      managerCnt.value = Number(managerCntFromApi) || 0;

      // 기간 플래그 갱신 (목록이 있으면 첫 KPI로 호출)
      const firstKpiId = kpiList.value?.[0]?.kpiId;
      await refreshKpiConfigTermYn(firstKpiId);

      // KPI 진행상태 목록 조회
      await fetchKpiStatusListData();

      // 공유받은 KPI들의 실적/달성률 동기화
      await syncKpiCumulativeData();

      // 선택된 KPI 처리
      if (preserveSelection && previousSelectedKpiId) {
        // 새로 조회한 목록에서 같은 KPI 찾아서 다시 선택
        const updatedKpi = kpiList.value.find(kpi => kpi.kpiId === previousSelectedKpiId);
        if (updatedKpi) {
          selectedKpi.value = updatedKpi;
        } else {
          // 삭제된 경우 초기화
          selectedKpi.value = null;
          resetDetailData();
        }
      } else {
        // 상세 패널 초기화
        selectedKpi.value = null;
        resetDetailData();
      }
    } catch (error) {
      console.error('KPI 데이터 조회 실패:', error);
      kpiList.value = [];
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * KPI 순서 변경
   * @param oldIndex - 이동할 항목의 현재 인덱스
   * @param newIndex - 이동할 목표 인덱스
   * @description 드래그 앤 드롭으로 KPI 순서를 변경할 때 사용
   */
  const reorderKpiList = (oldIndex: number, newIndex: number) => {
    const list = [...kpiList.value];
    const [movedItem] = list.splice(oldIndex, 1);
    list.splice(newIndex, 0, movedItem);
    kpiList.value = list;
  };

  /**
   * 검색 실행
   * @description 현재 선택된 검색 조건으로 KPI 목록과 구성원 트리를 조회
   */
  const handleSearch = async () => {
    // accessType 갱신: team 캐시 기반으로 판별 (team 탭 잔류 방지)
    if (selectedMember.value === loginUserInfo.value?.userId) {
      selectedMemberAccessType.value = 'self';
    } else {
      const isMyTeamMember = teamMemberCache.value.some(m => m.searchUserId === selectedMember.value);
      selectedMemberAccessType.value = isMyTeamMember ? 'team' : 'org';
    }

    // ============================================================
    // [열람 도메인 판정]
    // ⚠️ 헤더 검색을 "리더/본인만 사용한다"고 전제하면 안 된다.
    //    구성원(team)·공유(share) 탭에서 대상자를 고른 뒤에도 헤더 검색은 그대로 쓸 수 있고,
    //    여기서 도메인을 무조건 양쪽 허용으로 되돌리면 KPI만 공유받은 대상자의 OKR까지 열린다.
    //    (연도만 바꿔 검색 → 목표유형 셀렉트에 OKR/전체가 되살아나던 누수)
    //  → 선택 경로(selectedMemberDomainScope)를 기준으로 판정하고,
    //    team/share 경유 선택은 적용된 연도 기준으로 권한을 다시 확인한다.
    //
    // ⚠️ selectedMemberAccessType 이 아니라 selectedMemberDomainScope 로 판정하는 이유:
    //    accessType 은 바로 위에서 team 캐시 기준으로 재계산되므로,
    //    조직탭에서 고른 사용자도 team 으로 바뀌어 두 번째 검색부터 열람이 잘못 축소된다.
    // ============================================================
    const { scope, userId: scopeUserId } = selectedMemberDomainScope.value;
    // scope 는 기록 당시의 대상자에게만 유효 — 헤더 셀렉트로 다른 사람을 고르면 무시하고 개방
    const isScopedMember = scope !== 'open' && scopeUserId && scopeUserId === selectedMember.value;

    if (selectedMember.value === loginUserInfo.value?.userId) {
      selectedMemberDomains.value = { kpi: true, okr: true };
    } else if (isScopedMember) {
      selectedMemberDomains.value = await resolveMemberDomains(selectedMember.value, scope as 'team' | 'share');
    } else {
      // 본인/조직탭/리더/관리자 경로 → 양쪽 허용
      selectedMemberDomains.value = { kpi: true, okr: true };
    }
    // 허용 도메인에 맞춰 목표유형 필터 보정 (제한된 경우 허용된 첫 값으로 교체됨)
    enforceGoalTypeForDomains();
    const canKpi = selectedMemberDomains.value.kpi;
    const canOkr = selectedMemberDomains.value.okr;

    // 합의 기능 사용여부는 연도에 따라 바뀔 수 있으므로 검색 시점에도 갱신
    await refreshAgreementYn(); // 합의 기능 사용여부 갱신

    // KPI 목록 조회 (열람 권한 있을 때만 — 없으면 비움)
    if (canKpi) {
      await fetchKpiData();
    } else {
      kpiList.value = [];
    }
    await fetchMemberTree(selectedDept.value || undefined, undefined);

    activeMember.value = selectedMember.value || loginUserInfo.value?.userId || ''; // 활성 구성원 동기화

    // ============================================================
    // [OKR 갱신] — 열람 권한(canOkr) 있을 때만 조회.
    // - 권한 없으면 조회하지 않고 OKR 상태/캐시를 비워 정보 노출을 차단한다.
    // - 검색 시점에 OKR도 갱신해야 검색 결과와 OBJ 목록이 항상 일치한다
    //   (분기 change 안 해도 OBJ가 보이도록 — kpiOkrMap/unlinkedOkrList 재조회).
    // ============================================================
    if (canOkr) {
      await okrStore.fetchCycleList();
      okrStore.clearGoalOkrCache();
      await okrStore.fetchUnlinkedOkrList();
    } else {
      okrStore.resetOkrForMember();
    }
  };

  /**
   * MemberPanel 구성원 클릭 전용 즉시 검색
   *
   * @description
   * MemberPanel(좌측 조직도/구성원/공유 탭)에서 구성원을 클릭하면
   * 헤더 검색 버튼(돋보기)을 누르지 않아도 즉시 조건을 확정(commit)하고
   * KPI 목록 + OKR 분기를 재조회하여 중앙 테이블을 갱신한다.
   *
   * ★ 기존 handleSearch()와의 차이점 ★
   * - handleSearch()는 fetchMemberTree(dept, member)를 호출하므로
   *   특정 구성원 1명만 전달하면 트리가 그 사람 1명으로 축소된다.
   * - 이 함수는 MemberPanel에서 호출되므로 트리를 축소하면 안 된다.
   *   → fetchMemberTree(dept, undefined) 형태로 호출하여 부서 범위의 전체 트리를 유지한다.
   *
   * ★ 중복 클릭(연타) 방지 ★
   * - 이미 동일 구성원이 applied 상태인 경우 API 재호출을 스킵한다.
   *
   * @param userId - 클릭한 구성원의 userId
   */
  const handleSearchFromPanel = async (userId: string) => {
    // 가드: 이미 같은 구성원이 applied 상태이면 스킵 (연타 방지)
    if (selectedMember.value === userId && activeMember.value === userId) return;

    // 1. draft → applied 복사 (연도/부서/목표유형/구성원 모두 확정)
    applySearchConditions();

    // 1-1. 선택 대상자 허용 도메인에 맞춰 목표유형 필터 보정
    //      (selectedMemberDomains 는 MemberPanel 선택 핸들러에서 이미 세팅됨)
    enforceGoalTypeForDomains();
    const canKpi = selectedMemberDomains.value.kpi;
    const canOkr = selectedMemberDomains.value.okr;

    // 2. KPI 목록 조회 (열람 권한 있을 때만 — 없으면 비움)
    await refreshAgreementYn();
    if (canKpi) {
      await fetchKpiData();
    } else {
      kpiList.value = [];
    }

    // 3. 멤버트리 재조회 — 부서가 변경된 경우에만 트리 갱신
    //    같은 부서 내 다른 구성원 선택 시에는 트리를 유지하여
    //    접기/펼치기 상태와 스크롤 위치를 보존한다.
    const prevDept = selectedDept.value || undefined;
    if (memberTree.value.length === 0) {
      await fetchMemberTree(prevDept, undefined);
    }

    // 4. activeMember 동기화 (UI 하이라이트 유지)
    activeMember.value = userId;

    // 5~6. OKR — 열람 권한(canOkr) 있을 때만 조회.
    //       권한 없으면 조회하지 않고 OKR 상태/캐시를 비워 정보 노출을 차단한다.
    //       (구성원이 바뀌면 OKR 목록도 달라지므로 캐시 비우고 새로 조회)
    if (canOkr) {
      await okrStore.fetchCycleList();
      okrStore.clearGoalOkrCache();
      await okrStore.fetchUnlinkedOkrList();
    } else {
      okrStore.resetOkrForMember();
    }
  };

  // ============================================
  // 실적상세 - 기본 기능
  // ============================================

  /**
   * KPI 선택
   * @param kpi - 선택할 KPI 항목
   * @description KPI를 선택하고 실적상세 패널을 초기화 (월별 실적 탭으로 전환)
   */
  const selectKpi = async (kpi: KpiItem) => {
    selectedKpi.value = kpi;
    resetDetailData();
    activeDetailTab.value = 'monthly';
  };

  /**
   * 탭 전환
   * @param tab - 전환할 탭 ('monthly' | 'task' | 'okr')
   * @description 실적상세 패널의 탭을 전환
   */
  const changeDetailTab = async (tab: 'monthly' | 'task' | 'okr') => {
    activeDetailTab.value = tab;
  };

  // ============================================
  // 초기화
  // ============================================

  /**
   * Store 초기화
   * @description 페이지 로드 시 호출되어 모든 초기 데이터를 로드
   * 1. 초기값 설정
   * 2. 로그인 사용자 정보 및 연도 옵션 로드
   * 3. 로그인 사용자 정보 저장
   * 4. MemberTree 조회 → 리더 여부 판별 (트리 구성원 2명 이상이면 리더)
   * 5. 부서/구성원 옵션 로드
   * 6. KPI 목록 조회
   * 7. OKR 분기 목록 조회
   */
  const initialize = async () => {
    isLoading.value = true;
    const currentYear = new Date().getFullYear().toString();
    // 공통 store 에서 기준년도 복원 (없으면 현재년도)
    const savedYear = commonStore.findYear || currentYear;
    try {
      // 1. 초기값 설정 (applied + draft 동기화)
      selectedYear.value = savedYear;
      selectedDept.value = '';
      selectedMember.value = '';
      draftYear.value = savedYear;
      draftDept.value = '';
      draftMember.value = '';
      draftGoalTypeFilter.value = 'all';
      goalTypeFilter.value = 'all';
      selectedKpi.value = null;
      activeDetailTab.value = 'monthly';
      resetDetailData();

      // 2. 로그인 사용자 정보 조회 및 연도 옵션 로드
      const [userInfoResult] = await Promise.all([kpiApi.fetchLoginUserInfo(), loadYearOptions()]);

      // 3. 로그인 사용자 정보 저장
      const userInfo = userInfoResult?.userinfo;
      if (userInfo) {
        loginUserInfo.value = {
          userId: userInfo.userId || '',
          userNm: userInfo.userNm || '',
          deptId: userInfo.deptId || '',
          deptNm: userInfo.deptNm || '',
          posNm: userInfo.posNm || '',
        };
      }

      // 4. MemberTree 조회 → 리더 여부 판별
      await fetchMemberTree();
      isLeader.value = memberTree.value.some(m => m.parentId === loginUserInfo.value?.userId);

      // 5. 부서/구성원 옵션 로드
      await loadDeptOptions();
      await loadMemberOptions(loginUserInfo.value?.deptId || '', true);

      // 6. KPI 목록 조회
      await refreshAgreementYn();
      await fetchKpiData();

      // 7. OKR 분기 목록 조회
      await okrStore.fetchCycleList();
    } catch (error) {
      console.error('KpiStore 초기화 실패:', error);
    } finally {
      isLoading.value = false;
    }
  };

  // ============================================
  // Watch 설정
  // ============================================

  if (!watchersRegistered) {
    watchersRegistered = true;

    // 연도 저장은 공통 store(useCommonStore)의 내부 watch 에서 쿠키/localStorage 동기화 처리

    // 부서 draft 변경 시 구성원 옵션 재로드
    // (UI에서 부서 셀렉트를 바꾸면 구성원 드롭다운 목록만 갱신, API 조회는 검색 버튼 클릭 시)
    watch(draftDept, async (newDeptId, oldDeptId) => {
      if (newDeptId === oldDeptId) return;
      if (isLeader.value) {
        draftMember.value = ''; // 부서 변경 시 구성원 draft 초기화
        await loadMemberOptions(newDeptId);
        // 부서 변경 시 자동 검색 (selectedMember와 draftMember 동기화)
        applySearchConditions();
        await handleSearch();
      }
    });

    // 연도 변경 시 OKR 분기 목록 갱신 → 검색 버튼 클릭 시에만 갱신 (handleSearch에서 처리)
  }

  // ============================================
  // Return
  // ============================================

  return {
    // KPI 합의(Agreement) 플래그 갱신(외부에서 필요 시 호출)
    refreshAgreementYn,
    refreshKpiConfigTermYn,
    // 옵션 로드
    loadYearOptions,
    loadDeptOptions,
    loadMemberOptions,
    fetchMemberTree,
    fetchKpiStatusListData,
    loadMemberShareList,

    // KPI 목록
    fetchKpiData,
    reorderKpiList,
    handleSearch,
    handleCopyKpi, // KPI 복사 실행 (다른 연도로 마스터 행만 복사)

    // MemberPanel 구성원 클릭 전용 즉시 검색 (트리 유지, 중복 클릭 방지 포함)
    handleSearchFromPanel,

    // draft → applied 적용 (검색 버튼/액션 클릭 시 호출)
    applySearchConditions,

    // 실적상세 기본
    selectKpi,
    changeDetailTab,
    resetDetailData,

    // 초기화
    initialize,

    // 헬퍼 (외부에서 사용 가능)
    mapKpiListItem,
  };
};
